import React, { useState, useRef } from 'react';
import { Question, Exam, Category, Difficulty } from '../../types';

interface AdminCSVUploadProps {
    categories: Category[];
    exams: Exam[];
    onAddExam: (exam: Exam) => void;
    onUpdateExam: (exam: Exam) => void;
}

interface ParsedQuestion {
    stem: string;
    optionA: string;
    optionB: string;
    optionC: string;
    optionD: string;
    correct: string;
    explanation: string;
    difficulty: string;
    subject: string;
    imageUrl?: string;
}

const AdminCSVUpload: React.FC<AdminCSVUploadProps> = ({
    categories,
    exams,
    onAddExam,
    onUpdateExam
}) => {
    const [uploadCategory, setUploadCategory] = useState(categories[0]?.id || '');
    const [uploadPaperId, setUploadPaperId] = useState('');
    const [uploadStatus, setUploadStatus] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);
    const [previewData, setPreviewData] = useState<ParsedQuestion[]>([]);
    const [columnMapping, setColumnMapping] = useState<Record<string, number>>({});
    const [showPreview, setShowPreview] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Complete CSV parser that handles multi-line cells inside quotes
    const parseCSV = (text: string): string[][] => {
        const rows: string[][] = [];
        let currentRow: string[] = [];
        let currentCell = '';
        let inQuotes = false;

        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            const nextChar = text[i + 1];

            if (char === '"') {
                if (inQuotes && nextChar === '"') {
                    // Escaped quote inside quoted cell
                    currentCell += '"';
                    i++;
                } else {
                    // Toggle quote state
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                // End of cell
                currentRow.push(currentCell.trim());
                currentCell = '';
            } else if ((char === '\n' || char === '\r') && !inQuotes) {
                // End of row (but only if not inside quotes)
                if (char === '\r' && nextChar === '\n') {
                    i++; // Skip the \n after \r
                }
                currentRow.push(currentCell.trim());
                if (currentRow.some(cell => cell !== '')) {
                    rows.push(currentRow);
                }
                currentRow = [];
                currentCell = '';
            } else if (char === '\n' && inQuotes) {
                // Newline inside quoted cell - preserve it as actual newline
                currentCell += '\n';
            } else if (char !== '\r') {
                // Regular character (skip \r)
                currentCell += char;
            }
        }

        // Don't forget the last cell and row
        if (currentCell || currentRow.length > 0) {
            currentRow.push(currentCell.trim());
            if (currentRow.some(cell => cell !== '')) {
                rows.push(currentRow);
            }
        }

        console.log('Parsed rows:', rows.length);
        console.log('First row (headers):', rows[0]);
        if (rows[1]) console.log('Second row (first data):', rows[1].map(c => c.substring(0, 50) + '...'));

        return rows;
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const text = event.target?.result as string;
                const allRows = parseCSV(text);

                if (allRows.length < 2) {
                    setUploadStatus({ type: 'error', message: 'CSV file is empty or missing data rows.' });
                    return;
                }

                // First row is headers
                const headers = allRows[0].map(h => h.toLowerCase());
                console.log('CSV Headers detected:', headers);

                // Find column indices
                const stemIdx = headers.findIndex(h => h.includes('stem') || h.includes('text') || h.includes('question'));
                const optAIdx = headers.findIndex(h => h.includes('option_a') || h.includes('option a') || h === 'a');
                const optBIdx = headers.findIndex(h => h.includes('option_b') || h.includes('option b') || h === 'b');
                const optCIdx = headers.findIndex(h => h.includes('option_c') || h.includes('option c') || h === 'c');
                const optDIdx = headers.findIndex(h => h.includes('option_d') || h.includes('option d') || h === 'd');
                const correctIdx = headers.findIndex(h => h.includes('correct') || h.includes('answer'));
                const explIdx = headers.findIndex(h => h.includes('explanation'));
                const diffIdx = headers.findIndex(h => h.includes('difficulty'));
                const subjIdx = headers.findIndex(h => h.includes('subject'));
                const imgIdx = headers.findIndex(h => h.includes('image') || h.includes('img_url') || h.includes('image_url'));

                const mapping = { stem: stemIdx, optA: optAIdx, optB: optBIdx, optC: optCIdx, optD: optDIdx, correct: correctIdx, explanation: explIdx, difficulty: diffIdx, subject: subjIdx, image: imgIdx };
                setColumnMapping(mapping);
                console.log('Column mapping:', mapping);

                if (stemIdx === -1 || optAIdx === -1 || correctIdx === -1) {
                    setUploadStatus({ type: 'error', message: `Missing required columns. Found: ${headers.join(', ')}` });
                    return;
                }

                // Parse preview data (first 5 rows after header)
                const parsed: ParsedQuestion[] = [];
                const maxPreview = Math.min(allRows.length, 6); // header + 5 rows
                for (let i = 1; i < maxPreview; i++) {
                    const row = allRows[i];
                    if (!row || row.length < 3) continue;

                    parsed.push({
                        stem: row[stemIdx] || '',
                        optionA: optAIdx !== -1 ? row[optAIdx] || '' : '',
                        optionB: optBIdx !== -1 ? row[optBIdx] || '' : '',
                        optionC: optCIdx !== -1 ? row[optCIdx] || '' : '',
                        optionD: optDIdx !== -1 ? row[optDIdx] || '' : '',
                        correct: correctIdx !== -1 ? row[correctIdx]?.toUpperCase().replace(/[^A-D]/g, '') || 'A' : 'A',
                        explanation: explIdx !== -1 ? row[explIdx] || '' : '',
                        difficulty: diffIdx !== -1 ? row[diffIdx] || 'Medium' : 'Medium',
                        subject: subjIdx !== -1 ? row[subjIdx] || '' : '',
                        imageUrl: imgIdx !== -1 ? row[imgIdx] : undefined
                    });
                }

                setPreviewData(parsed);
                setShowPreview(true);
                setUploadStatus({
                    type: 'info',
                    message: `Found ${allRows.length - 1} questions. Preview first ${parsed.length} below. Click "Confirm Upload" to proceed.`
                });

            } catch (err: any) {
                setUploadStatus({ type: 'error', message: err.message || 'Failed to parse CSV' });
            }
        };
        reader.readAsText(file);
    };

    const handleConfirmUpload = () => {
        const file = fileInputRef.current?.files?.[0];
        if (!file || !uploadPaperId || !uploadCategory) {
            setUploadStatus({ type: 'error', message: 'Please fill all fields and select a file.' });
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const text = event.target?.result as string;
                const allRows = parseCSV(text);
                const headers = allRows[0].map(h => h.toLowerCase());

                const stemIdx = columnMapping.stem ?? headers.findIndex(h => h.includes('stem') || h.includes('text') || h.includes('question'));
                const optAIdx = columnMapping.optA ?? headers.findIndex(h => h.includes('option_a'));
                const optBIdx = columnMapping.optB ?? headers.findIndex(h => h.includes('option_b'));
                const optCIdx = columnMapping.optC ?? headers.findIndex(h => h.includes('option_c'));
                const optDIdx = columnMapping.optD ?? headers.findIndex(h => h.includes('option_d'));
                const correctIdx = columnMapping.correct ?? headers.findIndex(h => h.includes('correct'));
                const explIdx = columnMapping.explanation ?? headers.findIndex(h => h.includes('explanation'));
                const diffIdx = columnMapping.difficulty ?? headers.findIndex(h => h.includes('difficulty'));
                const subjIdx = columnMapping.subject ?? headers.findIndex(h => h.includes('subject'));
                const imgIdx = columnMapping.image ?? headers.findIndex(h => h.includes('image'));

                const newQuestions: Question[] = [];

                for (let i = 1; i < allRows.length; i++) {
                    const row = allRows[i];
                    if (!row || row.length < 3) continue;

                    newQuestions.push({
                        id: `csv-${Date.now()}-${i}`,
                        text: row[stemIdx] || `Question ${i}`,
                        imageUrl: imgIdx !== -1 && row[imgIdx] ? row[imgIdx] : undefined,
                        options: [
                            { id: 'A', text: optAIdx !== -1 ? row[optAIdx] || '' : '' },
                            { id: 'B', text: optBIdx !== -1 ? row[optBIdx] || '' : '' },
                            { id: 'C', text: optCIdx !== -1 ? row[optCIdx] || '' : '' },
                            { id: 'D', text: optDIdx !== -1 ? row[optDIdx] || '' : '' },
                        ],
                        correctOptionId: correctIdx !== -1 ? row[correctIdx]?.toUpperCase().replace(/[^A-D]/g, '') || 'A' : 'A',
                        explanation: explIdx !== -1 ? row[explIdx] || '' : '',
                        difficulty: (diffIdx !== -1 ? row[diffIdx] : 'Medium') as Difficulty,
                        subject: subjIdx !== -1 ? row[subjIdx] || '' : ''
                    });
                }

                if (newQuestions.length === 0) {
                    setUploadStatus({ type: 'error', message: 'No valid questions found in CSV.' });
                    return;
                }

                const existingExam = exams.find(e => e.id === uploadPaperId);
                if (existingExam) {
                    const updatedExam: Exam = {
                        ...existingExam,
                        questions: [...existingExam.questions, ...newQuestions],
                        totalQuestions: existingExam.questions.length + newQuestions.length
                    };
                    onUpdateExam(updatedExam);
                    setUploadStatus({ type: 'success', message: `✅ Added ${newQuestions.length} questions to existing paper "${existingExam.title}".` });
                } else {
                    const newExam: Exam = {
                        id: uploadPaperId,
                        title: uploadPaperId,
                        category: uploadCategory,
                        durationMinutes: 60,
                        positiveMarks: 2,
                        negativeMarks: 0.66,
                        totalQuestions: newQuestions.length,
                        questions: newQuestions
                    };
                    onAddExam(newExam);
                    setUploadStatus({ type: 'success', message: `✅ Created new paper "${uploadPaperId}" with ${newQuestions.length} questions.` });
                }

                // Reset
                setShowPreview(false);
                setPreviewData([]);
                if (fileInputRef.current) fileInputRef.current.value = '';

            } catch (err: any) {
                setUploadStatus({ type: 'error', message: err.message || 'Failed to upload CSV' });
            }
        };
        reader.readAsText(file);
    };

    const cancelPreview = () => {
        setShowPreview(false);
        setPreviewData([]);
        setUploadStatus(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <div className="max-w-4xl mx-auto bg-white dark:bg-slate-800 p-8 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
            <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-6">Bulk Upload Questions via CSV</h3>
            <div className="space-y-6">
                {/* Instructions */}
                <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-800 dark:text-blue-300">
                    <p className="font-semibold mb-2"><i className="fas fa-info-circle mr-2"></i> CSV Format</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                        <div>
                            <p className="font-medium mb-1">Required columns:</p>
                            <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">stem, option_a, option_b, option_c, option_d, correct</code>
                        </div>
                        <div>
                            <p className="font-medium mb-1">Optional columns:</p>
                            <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">explanation, difficulty, subject, image_url</code>
                        </div>
                    </div>
                </div>

                {/* Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Category</label>
                        <select
                            value={uploadCategory}
                            onChange={(e) => setUploadCategory(e.target.value)}
                            className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                        >
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Paper ID</label>
                        <input
                            type="text"
                            value={uploadPaperId}
                            onChange={(e) => setUploadPaperId(e.target.value)}
                            list="paper-options"
                            className="w-full p-2.5 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-400"
                            placeholder="Select or Type Paper ID"
                            required
                        />
                        <datalist id="paper-options">
                            {exams.filter(e => e.category === uploadCategory).map(e => (
                                <option key={e.id} value={e.id}>{e.title}</option>
                            ))}
                        </datalist>
                    </div>
                </div>

                {/* File Input */}
                <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Select CSV File</label>
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept=".csv"
                        onChange={handleFileSelect}
                        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 dark:file:bg-orange-900/50 dark:file:text-orange-300"
                    />
                </div>

                {/* Status Message */}
                {uploadStatus && (
                    <div className={`p-3 rounded-lg text-sm ${uploadStatus.type === 'error' ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
                        uploadStatus.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400' :
                            'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                        }`}>
                        {uploadStatus.message}
                    </div>
                )}

                {/* Preview Table */}
                {showPreview && previewData.length > 0 && (
                    <div className="border border-slate-200 dark:border-slate-600 rounded-lg overflow-hidden">
                        <div className="bg-slate-100 dark:bg-slate-700 px-4 py-2 font-semibold text-slate-700 dark:text-slate-200 text-sm">
                            <i className="fas fa-eye mr-2"></i> Preview (First {previewData.length} questions)
                        </div>
                        <div className="overflow-x-auto max-h-96">
                            <table className="w-full text-xs">
                                <thead className="bg-slate-50 dark:bg-slate-600 sticky top-0">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-slate-600 dark:text-slate-300">#</th>
                                        <th className="px-3 py-2 text-left text-slate-600 dark:text-slate-300 min-w-[200px]">Question</th>
                                        <th className="px-3 py-2 text-left text-slate-600 dark:text-slate-300">A</th>
                                        <th className="px-3 py-2 text-left text-slate-600 dark:text-slate-300">B</th>
                                        <th className="px-3 py-2 text-left text-slate-600 dark:text-slate-300">C</th>
                                        <th className="px-3 py-2 text-left text-slate-600 dark:text-slate-300">D</th>
                                        <th className="px-3 py-2 text-left text-slate-600 dark:text-slate-300">Ans</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                                    {previewData.map((q, idx) => (
                                        <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-700">
                                            <td className="px-3 py-2 text-slate-500">{idx + 1}</td>
                                            <td className="px-3 py-2 text-slate-800 dark:text-slate-200 max-w-[300px] truncate" title={q.stem}>{q.stem.substring(0, 100)}...</td>
                                            <td className="px-3 py-2 text-slate-600 dark:text-slate-300 max-w-[100px] truncate" title={q.optionA}>{q.optionA.substring(0, 30)}</td>
                                            <td className="px-3 py-2 text-slate-600 dark:text-slate-300 max-w-[100px] truncate" title={q.optionB}>{q.optionB.substring(0, 30)}</td>
                                            <td className="px-3 py-2 text-slate-600 dark:text-slate-300 max-w-[100px] truncate" title={q.optionC}>{q.optionC.substring(0, 30)}</td>
                                            <td className="px-3 py-2 text-slate-600 dark:text-slate-300 max-w-[100px] truncate" title={q.optionD}>{q.optionD.substring(0, 30)}</td>
                                            <td className="px-3 py-2 font-bold text-green-600">{q.correct}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Check if data looks wrong */}
                        {previewData.some(q => q.optionA.length > 100 || q.optionB.length > 100) && (
                            <div className="bg-yellow-50 dark:bg-yellow-900/30 p-3 text-yellow-700 dark:text-yellow-400 text-sm">
                                <i className="fas fa-exclamation-triangle mr-2"></i>
                                <strong>Warning:</strong> Some options are very long. Check if your CSV columns are correctly aligned!
                            </div>
                        )}
                    </div>
                )}

                {/* Action Buttons */}
                {showPreview ? (
                    <div className="flex gap-4">
                        <button
                            onClick={cancelPreview}
                            className="flex-1 bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-200 py-3 rounded-lg hover:bg-slate-300 dark:hover:bg-slate-500 font-medium transition-colors"
                        >
                            <i className="fas fa-times mr-2"></i> Cancel
                        </button>
                        <button
                            onClick={handleConfirmUpload}
                            disabled={!uploadPaperId}
                            className="flex-1 bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-medium transition-colors disabled:opacity-50"
                        >
                            <i className="fas fa-check mr-2"></i> Confirm Upload
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        disabled={!fileInputRef.current?.files?.length}
                        className="w-full bg-slate-900 dark:bg-slate-600 text-white py-3 rounded-lg hover:bg-slate-800 dark:hover:bg-slate-500 font-medium transition-colors disabled:opacity-50"
                    >
                        <i className="fas fa-upload mr-2"></i> Select a CSV file to preview
                    </button>
                )}
            </div>
        </div>
    );
};

export default AdminCSVUpload;
